import { useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import modalInformationAtom, { ModalInformationAtom } from 'atom/modalInformationAtom';

type OriginModal = {
  isModalOpen: boolean;
  leftBtnName?: string;
  rightBtnName: string;
  message: string;
  TextComponent: JSX.Element;
  handleClickLeftBtn: (() => void) | null;
  handleClickRightBtn: (() => void) | null;
};

type Empty = typeof empty;

type BizmemberJoin = {
  leftBtnName: '아니오';
  rightBtnName: '예';
  message: string;
  handleClickLeftBtn: () => void;
  handleClickRightBtn: () => void;
};

type UNITEDMEMBER_JOIN = {
  leftBtnName: '아니오';
  rightBtnName: '예';
  message: string;
  handleClickRightBtn: () => void;
};

type EmailCheck = {
  rightBtnName: '확인';
  message: string;
  handleClickRightBtn: () => void;
};
type TwoButtonCase = {
  leftBtnName: string;
  rightBtnName: string;
  message: string;
  handleClickLeftBtn: () => void;
  handleClickRightBtn: () => void;
};

type OneButtonCase = {
  leftBtnName: string;
  rightBtnName: string;
  message: string;
  handleClickRightBtn: () => void;
};

type JustCheck = {
  rightBtnName: string;
  message: string;
  powerOK?: boolean;
  handleClickRightBtn: () => void;
};

type ModalInformation =
  | Empty
  | UNITEDMEMBER_JOIN
  | BizmemberJoin
  | EmailCheck
  | OneButtonCase
  | TwoButtonCase
  | JustCheck;

const empty = {
  isModalOpen: false,
  leftBtnName: '',
  rightBtnName: '',
  message: '',
  handleClickRightBtn: null
};

const useGlobalModal = (): OriginModal => {
  const [globalModal, setGlobalModal] = useAtom(modalInformationAtom);
  const [modalInformation, setModalInformation] = useState<ModalInformation>(empty);

  const handleClickBtn = useCallback(
    (func?: () => void) => () => {
      func && func();
      setModalInformation(empty);
      setGlobalModal(null);
    },
    [setGlobalModal]
  );

  useEffect(() => {
    if (!globalModal) {
      return;
    }

    const { type } = globalModal;

    if (type === 'BIZMEMBER_JOIN') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '비즈멤버십 미가입 계정입니다.\n멤버십가입을 진행하시겠습니까?',
        handleClickLeftBtn: handleClickBtn(globalModal.handleClickLeftBtn),
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'UNITEDMEMBER_JOIN') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '엑스바디 통합회원 가입 후 이용가능합니다.\n통합회원가입을 진행하시겠습니까?',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'EMAIL_CHECK') {
      setModalInformation({
        rightBtnName: '확인',
        message: '1개 이상의 서비스와 연동이 되어야 합니다.',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'SENDEMAIL_CODE_CONFIRM') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '인증코드를 발송하시겠습니까? ',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
    }

    if (type === 'SENDEMAIL_CODE_ALERT') {
      setModalInformation({
        rightBtnName: '확인',
        message: '인증코드를 발송하였습니다.\n이메일로 받으신 인증코드를 입력해주세요.',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'SENDEMAIL_CODE_ERROR') {
      setModalInformation({
        rightBtnName: '확인',
        message: '인증시간을 초과하였습니다.\n인증코드를 다시 발송하여 주세요.',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'NOSENDEMAIL_CODE') {
      setModalInformation({
        rightBtnName: '확인',
        message: '인증코드 발송 후 인증코드를 입력해주세요.',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'TERMS_ERROR') {
      setModalInformation({
        rightBtnName: '확인',
        message: '필수항목에 동의하여주세요.',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }
    if (type === 'ITEM_ERROR' && globalModal.item) {
      setModalInformation({
        rightBtnName: '확인',
        message: `${globalModal.item}를 입력하여주세요.`,
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'ITEM_COMPLETE_ERROR' && globalModal.item) {
      setModalInformation({
        rightBtnName: '확인',
        message: `${globalModal.item}을 완료하여주세요.`,
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'WARNING' && globalModal.message) {
      setModalInformation({
        rightBtnName: '예',
        message: globalModal.message,
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'WARNING_AND_NEXT' && globalModal.message) {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: globalModal.message,
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'FILE_ERROR') {
      setModalInformation({
        rightBtnName: '확인',
        message: `가능한 확장자는 jpg/png/pdf 입니다.`,
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'CONFIRM' && globalModal.message) {
      setModalInformation({
        powerOK: globalModal.powerOK ? true : false,
        rightBtnName: '예',
        message: globalModal.message,
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'CHECK_FREE') {
      setModalInformation({
        rightBtnName: '확인',
        message: `무료 티켓과 다른 티켓을 동시에 구매 할 수 없습니다.`,
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'SUBSCRIBE_FREE') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: `무료 티켓으로 구독하시겠습니까?`,
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'JOIN_CONFIRM') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '해당 계약 종류로 엑스바디 비즈멤버십을 가입하시겠습니까?',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
    }

    if (type === 'JOIN_ERROR') {
      setModalInformation({
        rightBtnName: '확인',
        message: `회원가입이 완료되지 않았습니다.\n다시 한번 ‘다음’ 버튼을 클릭 해주세요.`,
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'ACCOUNT_LINK') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '계정연동요청을 승인하시겠습니까?',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }
    if (type === 'ACCOUNT_UNLINK') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '계정연동을 해지하시겠습니까?',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'MEMBERSHIP_UNSUBSCRIBE') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '해당 멤버십을 해지하시겠습니까?',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'ACCOUNT_LINK_REQUEST') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '계정연동요청 하시겠습니까? 상대방의 수락 이 필요합니다.',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'WITHDRAW_MEMBER') {
      setModalInformation({
        rightBtnName: '확인',
        message: '고객센터에 문의해주세요. ​',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'REMOVE_CART') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '장바구니에서 제거됩니다. 삭제하시겠습니까?',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }
    if (type === 'API_ERROR' && globalModal.message) {
      setModalInformation({
        rightBtnName: '확인',
        message: globalModal.message,
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'NOT_OPEN') {
      setModalInformation({
        rightBtnName: '확인',
        message: '준비중 입니다. ​',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'AGREE_CHECK') {
      setModalInformation({
        rightBtnName: '확인',
        message: '개인정보 제공에 동의 해주세요. ​',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'CARD_CHECK') {
      setModalInformation({
        rightBtnName: '확인',
        message: '카드 정보를 확인해주세요. ​',
        handleClickRightBtn: handleClickBtn()
      });
      return;
    }

    if (type === 'PAY_SUCCESS') {
      setModalInformation({
        rightBtnName: '확인',
        message: '결제가 성공되었습니다.',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'CHANGE_SUCCESS') {
      setModalInformation({
        rightBtnName: '확인',
        message: '변경이 완료되었습니다.',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'PAY_CANCEL') {
      setModalInformation({
        rightBtnName: '확인',
        message: '결제가 실패되었습니다.',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'UNSUBSCRIBE_SUCCESS') {
      setModalInformation({
        rightBtnName: '확인',
        message: '해지되었습니다.',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'CHOICE_CATEGORY') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '해당 항목을 선택하시겠습니까?',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
    }

    if (type === 'MEMBERSHIP_SUBSCRIBE') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '해당 멤버십을 결제하시겠습니까?',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
    }

    if (type === 'CHANGE_CARD') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '입력하신 정보로 카드를 변경하시겠습니까?',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
    }

    if (type === 'LOGIN_CONFIRM') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '로그인 후 이용 가능합니다.\n로그인 하시겠습니까?',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }
    if (type === 'DELETE_MEMBERSHIP') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message: '해당장비를 삭제하시겠습니까?​',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }

    if (type === 'CHECK_MEMBERSHIP') {
      setModalInformation({
        leftBtnName: '아니오',
        rightBtnName: '예',
        message:
          '이미 사용하고 있는 멤버십입니다.\n기존사용하던 멤버십은 해제됩니다.\n그래도 사용하시겠습니까?​',
        handleClickRightBtn: handleClickBtn(globalModal.handleClickRightBtn)
      });
      return;
    }
  }, [globalModal, handleClickBtn, setModalInformation]);

  return {
    handleClickLeftBtn: handleClickBtn(),
    ...modalInformation,
    isModalOpen: !!globalModal,
    TextComponent: getTextComponent(modalInformation, globalModal)
  };
};

export default useGlobalModal;

const getTextComponent = (
  modalInformation: ModalInformation,
  globalModal: ModalInformationAtom
) => {
  return <pre>{modalInformation.message}</pre>;
};
